"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
  getUsers,
  createUser,
  updateUser,
  updateUserStatus,
  getUsersByParentId,
  resetUserState,
  setActiveTab,
} from "../../slices/userSlice"
import { getAllStates, getCitiesByState } from "../../slices/locationSlice"
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputAdornment,
  Select,
  MenuItem,
  CircularProgress,
  Menu,
  Paper,
  ListItemIcon,
  InputLabel,
  FormLabel,
  Divider,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Percent as PercentIcon,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  CreditCard as CreditCardIcon,
  Update as UpdateIcon,
  Block as BlockIcon,
  Link as LinkIcon,
  Settings,
  List as ListIcon,
} from "@mui/icons-material"
import UplinkDialog from "./UplinkDialog"
import UplinkViewDialog from "./UplinkViewDialog" // New import

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.5),
}))

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  fontWeight: 600,
  padding: theme.spacing(1.5),
}))

const RoleBadge = styled(Chip)(({ role, theme }) => ({
  backgroundColor:
    role === "admin"
      ? theme.palette.error.main
      : role === "reseller"
        ? theme.palette.warning.main
        : theme.palette.info.main,
  color: "white",
  fontWeight: 500,
  fontSize: "0.75rem",
}))

const StatusBadge = styled(Chip)(({ status, theme }) => ({
  backgroundColor:
    status === "active"
      ? theme.palette.success.main
      : status === "suspended"
        ? theme.palette.warning.main
        : theme.palette.error.main,
  color: "white",
  fontWeight: 500,
  fontSize: "0.75rem",
}))

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "white",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  textTransform: "none",
  padding: "4px 12px",
  minWidth: "auto",
}))

const UserManagement = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth || {})
  const {
    users = [],
    loading = false,
    error = null,
    success = false,
    activeTab = "active",
    pagination = {
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      pageSize: 50,
      hasNextPage: false,
      hasPrevPage: false,
    },
  } = useSelector((state) => state.users || {})
  const { states = [] } = useSelector((state) => state.locations || {})
  const [actionLoading, setActionLoading] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [dialogMode, setDialogMode] = useState("create")
  const [currentUser, setCurrentUser] = useState(null)
  const [formData, setFormData] = useState({
    name: "",
    userName: "",
    mobile: "",
    cuttingPercentage: "",
    email: "",
    password: "",
    role: "user",
    state: "",
    city: "",
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [userToManage, setUserToManage] = useState(null)
  const [associatedUsers, setAssociatedUsers] = useState([])
  const [loadingAssociatedUsers, setLoadingAssociatedUsers] = useState(false)
  const [loadingStates, setLoadingStates] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)
  const [citiesList, setCitiesList] = useState([])
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [stateMap, setStateMap] = useState({})
  const [cityMap, setCityMap] = useState({})
  const [uplinkDialogOpen, setUplinkDialogOpen] = useState(false)
  const [uplinkViewDialogOpen, setUplinkViewDialogOpen] = useState(false) // New state for view dialog

  // Add these state variables below `const [uplinkViewDialogOpen, setUplinkViewDialogOpen] = useState(false)`
  const [userToConfigure, setUserToConfigure] = useState(null)
  const [configFormData, setConfigFormData] = useState({
    numberOfImages: "2", // Default from image
    numberOfPdfs: "1", // Default from image
    numberOfVideos: "1", // Default from image
    domDemoMaxCampaignAllowed: "3", // Default from image
    domMaxCampaignAllowed: "15", // Default from image
    intDemoMaxCampaignAllowed: "3", // Default from image
    intMaxCampaignAllowed: "15", // Default from image
    btnDemoMaxCampaignAllowed: "3",
    btnMaxCampaignAllowed: "15",
    autoRefund: "yes",
    canCreateDomestic: "yes",
    canCreateInternational: "yes",
    canCreateButton: "yes",
  })

  // Refs for controlling API calls on mount
  const hasFetchedUsersOnMount = useRef(false)
  const hasFetchedStatesOnMount = useRef(false)
  // Cache for cities by state ID to prevent redundant API calls for the form
  const cityCache = useRef({})

  // Check if current user is admin
  const isAdmin = userInfo?.user?.role === "admin"
  const isReseller = userInfo?.user?.role === "reseller"

  // Add the following function to get the initials of the user
  const getInitials = (name) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2)
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [roleFilter, setRoleFilter] = useState("")

  // Effect for fetching users: runs once on mount, then on activeTab change
  useEffect(() => {
    if (!hasFetchedUsersOnMount.current) {
      fetchUsers()
      hasFetchedUsersOnMount.current = true
    } else {
      // Only refetch if activeTab changes after initial mount
      fetchUsers()
    }
  }, [activeTab, dispatch])

  // Effect for fetching states: runs strictly once on mount
  useEffect(() => {
    if (!hasFetchedStatesOnMount.current) {
      fetchStates()
      hasFetchedStatesOnMount.current = true
    }
  }, [dispatch]) // Only dispatch as dependency to run once

  // Effect for fetching cities when a state is selected in the form
  useEffect(() => {
    if (formData.state) {
      fetchCities(formData.state)
    } else {
      setCitiesList([])
    }
  }, [formData.state, dispatch])

  // Effect for building stateMap (runs when states data changes)
  useEffect(() => {
    if (states.length > 0) {
      const stateMapping = {}
      states.forEach((state) => {
        stateMapping[state._id] = state.name
      })
      setStateMap(stateMapping)
    }
  }, [states])

  // Effect for updating cityMap when citiesList changes (from form interaction)
  // This ensures that cities selected in the form are added to the global cityMap
  useEffect(() => {
    if (citiesList.length > 0) {
      setCityMap((prevMap) => {
        const newMap = { ...prevMap }
        citiesList.forEach((city) => {
          newMap[city._id] = city.name
        })
        return newMap
      })
    }
  }, [citiesList])

  useEffect(() => {
    if (success) {
      handleCloseDialog()
      fetchUsers()
      dispatch(resetUserState())
    }
  }, [success, dialogMode, dispatch])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetUserState())
    }
  }, [error, dispatch])

  const fetchUsers = useCallback(() => {
    const params = {
      status: activeTab,
      page: currentPage,
      limit: pageSize,
    }

    if (searchQuery.trim()) {
      params.search = searchQuery.trim()
    }

    // Add role filter only for admin users
    if (roleFilter && isAdmin) {
      params.role = roleFilter
    }

    dispatch(getUsers(params))
  }, [dispatch, activeTab, currentPage, pageSize, searchQuery, roleFilter, isAdmin])

  useEffect(() => {
    if (searchQuery.trim()) {
      const timeoutId = setTimeout(() => {
        setCurrentPage(1) // Reset to first page when searching
        fetchUsers()
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    } else {
      // If search is cleared, fetch immediately
      setCurrentPage(1)
      fetchUsers()
    }
  }, [searchQuery])

  useEffect(() => {
    fetchUsers()
  }, [currentPage, pageSize, roleFilter, activeTab, fetchUsers])

  const fetchStates = () => {
    setLoadingStates(true)
    dispatch(getAllStates())
      .unwrap()
      .catch((err) => {
        toast.error("Failed to load states")
      })
      .finally(() => {
        setLoadingStates(false)
      })
  }

  const fetchCities = async (stateId) => {
    // Check cache before fetching
    if (cityCache.current[stateId] && cityCache.current[stateId].length > 0) {
      setCitiesList(cityCache.current[stateId])
      return
    }

    setLoadingCities(true)
    try {
      const data = await dispatch(getCitiesByState(stateId)).unwrap()
      setCitiesList(data.cities || [])
      cityCache.current[stateId] = data.cities || [] // Cache fetched cities
    } catch (err) {
      toast.error("Failed to load cities")
    } finally {
      setLoadingCities(false)
    }
  }

  const handleOpenCreateDialog = () => {
    setDialogMode("create")
    setCurrentUser(null)
    setFormData({
      name: "",
      userName: "",
      mobile: "",
      cuttingPercentage: "",
      email: "",
      password: "",
      role: "user",
      state: "",
      city: "",
    })
    setCitiesList([])
    setOpenDialog(true)
  }

  const handleOpenEditDialog = (user) => {
     handleCloseActionMenu();
    setDialogMode("edit")
    setCurrentUser(user)

    // Find if the user's state exists in the fetched states list
    const userStateExists = states.some((state) => state._id === user.state)
    const initialUserState = userStateExists ? user.state : ""

    setFormData({
      name: user.name || "",
      userName: user.userName || "",
      mobile: user.mobile || "",
      cuttingPercentage: user.cuttingPercentage || "",
      email: user.email || "",
      password: "", // Password field is empty when editing
      role: user.role || "user",
      state: initialUserState, // Use the validated state ID
      city: user?.city?._id || "",
    })

    // Only fetch cities if a valid state was found for the user
    if (initialUserState) {
      fetchCities(initialUserState)
    } else {
      // If the user's state is invalid/not found, clear cities list
      setCitiesList([])
      toast.warn("User's assigned state not found. Please select a new state.") // Inform user about data inconsistency
    }

    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setCurrentUser(null)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        city: "",
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setActionLoading(true)

    try {
      const userData = { ...formData }

      // Only include password in the update if it's not empty
      if (dialogMode === "edit" && !userData.password) {
        delete userData.password
      }

      if (dialogMode === "create") {
        dispatch(createUser(userData))
      } else if (currentUser) {
        dispatch(
          updateUser({
            id: currentUser._id,
            userData,
          }),
        )
      }
    } catch (error) {
      console.error("Failed to save user:", error)
      toast.error("Failed to save user")
    } finally {
      setActionLoading(false)
    }
  }

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
    handleDeleteUser()
  }
  // Modify handleOpenConfigDialog to initialize configFormData
  const handleOpenConfigDialog = (user) => {
    setUserToConfigure(user)
    setConfigDialogOpen(true)
    // Initialize configFormData with user's existing config or defaults
    setConfigFormData({
      numberOfImages: user.config?.numberOfImages !== undefined ? String(user.config.numberOfImages) : "2",
      numberOfPdfs: user.config?.numberOfPdfs !== undefined ? String(user.config.numberOfPdfs) : "1",
      numberOfVideos: user.config?.numberOfVideos !== undefined ? String(user.config.numberOfVideos) : "1",
      domDemoMaxCampaignAllowed:
        user.config?.domDemoMaxCampaignAllowed !== undefined ? String(user.config.domDemoMaxCampaignAllowed) : "3",
      domMaxCampaignAllowed:
        user.config?.domMaxCampaignAllowed !== undefined ? String(user.config.domMaxCampaignAllowed) : "15",
      intDemoMaxCampaignAllowed:
        user.config?.intDemoMaxCampaignAllowed !== undefined ? String(user.config.domDemoMaxCampaignAllowed) : "3",
      intMaxCampaignAllowed:
        user.config?.intMaxCampaignAllowed !== undefined ? String(user.config.intMaxCampaignAllowed) : "15",
      btnDemoMaxCampaignAllowed:
        user.config?.btnDemoMaxCampaignAllowed !== undefined ? String(user.config.btnDemoMaxCampaignAllowed) : "3",
      btnMaxCampaignAllowed:
        user.config?.btnMaxCampaignAllowed !== undefined ? String(user.config.btnMaxCampaignAllowed) : "15",
      autoRefund: user.config?.autoRefund ? "yes" : "no",
      canCreateDomestic: user.config?.canCreateDomestic ? "yes" : "no",
      canCreateInternational: user.config?.canCreateInternational ? "yes" : "no",
      canCreateButton: user.config?.canCreateButton ? "yes" : "no",
    })
  }
  const handleCloseConfigDialog = (user) => {
    setConfigDialogOpen(false)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const handleDeleteUser = async () => {
    if (userToDelete) {
      setActionLoading(true)
      try {
        await dispatch(updateUserStatus({ id: userToDelete._id, status: "deleted" })).unwrap()
        toast.success("User deleted successfully")
        handleCloseDeleteDialog()
        fetchUsers()
      } catch (error) {
        console.error("Failed to delete user:", error)
        toast.error("Failed to delete user")
      } finally {
        setActionLoading(false)
      }
    }
  }

  const handleOpenManageDialog = async (user) => {
    setUserToManage(user)
    setManageDialogOpen(true)

    if (user.role === "reseller") {
      setLoadingAssociatedUsers(true)
      try {
        const result = await dispatch(getUsersByParentId(user._id)).unwrap()
        setAssociatedUsers(result)
      } catch (error) {
        console.error("Failed to fetch associated users:", error)
        toast.error("Failed to fetch associated users")
        setAssociatedUsers([])
      } finally {
        setLoadingAssociatedUsers(false)
      }
    }
  }

  const handleCloseManageDialog = () => {
    setManageDialogOpen(false)
    setUserToManage(null)
    setAssociatedUsers([])
  }

  const handleSuspendUser = async (userId) => {
    setActionLoading(true)
    try {
      await dispatch(updateUserStatus({ id: userId, status: "suspended" })).unwrap()
      toast.success("User suspended successfully")
      handleCloseManageDialog()
      fetchUsers()
    } catch (error) {
      console.error("Failed to suspend user:", error)
      toast.error("Failed to suspend user")
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnsuspendUser = async (userId) => {
    setActionLoading(true)
    try {
      await dispatch(updateUserStatus({ id: userId, status: "active" })).unwrap()
      toast.success("User unsuspended successfully")
      fetchUsers()
    } catch (error) {
      console.error("Failed to unsuspend user:", error)
      toast.error("Failed to unsuspend user")
    } finally {
      setActionLoading(false)
    }
  }

  const handleRestoreUser = async (userId) => {
    setActionLoading(true)
    try {
      await dispatch(updateUserStatus({ id: userId, status: "active" })).unwrap()
      toast.success("User restored successfully")
      fetchUsers()
    } catch (error) {
      console.error("Failed to restore user:", error)
      toast.error("Failed to restore user")
    } finally {
      setActionLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    dispatch(setActiveTab(newValue))
  }

  const handleOpenActionMenu = (event, user) => {
    setActionMenuAnchorEl(event.currentTarget)
    setSelectedUser(user)
  }

  const handleCloseActionMenu = () => {
    setActionMenuAnchorEl(null)
    setSelectedUser(null)
  }

  const handleManageCredits = (userId) => {
    handleCloseActionMenu()
    navigate(`/credits/${userId}`)
  }

  // New handlers for Uplink View Dialog
  const handleOpenUplinkViewDialog = (user) => {
    handleCloseActionMenu()
    setSelectedUser(user)
    setUplinkViewDialogOpen(true)
  }

  const handleCloseUplinkViewDialog = () => {
    setUplinkViewDialogOpen(false)
    setSelectedUser(null)
  }

  // Handler to open UplinkDialog from UplinkViewDialog
  const handleOpenUplinkDialogFromView = () => {
    setUplinkViewDialogOpen(false) // Close view dialog
    setUplinkDialogOpen(true) // Open edit dialog
  }
  // Add new input change handler for config form
  const handleConfigInputChange = (e) => {
    const { name, value } = e.target
    setConfigFormData({
      ...configFormData,
      [name]: value,
    })
  }

  // Add new submit handler for config form
  const handleConfigSubmit = async (e) => {
    e.preventDefault()
    setActionLoading(true)
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000" // Use environment variable
      const token = userInfo?.token // Assuming token is available in userInfo

      // Convert string 'yes'/'no' to boolean true/false for autoRefund
      const payload = {
        ...configFormData,
        autoRefund: configFormData.autoRefund === "yes",
        canCreateDomestic: configFormData.canCreateDomestic === "yes",
        canCreateInternational: configFormData.canCreateInternational === "yes",
        canCreateButton: configFormData.canCreateButton === "yes",
      }

      const response = await fetch(`${apiUrl}/api/users/${userToConfigure._id}/config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Include authorization header
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || "Failed to update user configuration")
      }

      const data = await response.json()
      toast.success("User configuration updated successfully!")
      handleCloseConfigDialog()
      fetchUsers() // Refresh user list to reflect changes
    } catch (error) {
      console.error("Failed to save user config:", error)
      toast.error(error.message || "Failed to save user configuration")
    } finally {
      setActionLoading(false)
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  const handlePageSizeChange = (event) => {
    setPageSize(Number.parseInt(event.target.value, 10))
    setCurrentPage(1) // Reset to first page when changing page size
  }

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value)
    setCurrentPage(1) // Reset to first page when changing filter
  }

  // const filteredUsers = users.filter(
  //   (user) =>
  //     user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     user.role?.toLowerCase().includes(searchQuery.toLowerCase()),
  // )

  const getLocationString = (user) => {
    console.log("user in getLocationString:", user) // Debug log
    const stateName = user?.city?.stateId?.name ? user.city.stateId.name  : "Unknown State" 
    //const stateName = user.city ? stateMap[user.state]  "Unknown State" 
    // Use cityMap for display. If city is not in map, it will be "Unknown City"
    const cityName = user?.city?.name ? user?.city.name : "Unknown City" 
    const phoneNumber = user.mobile || "N/A"

    let locationStr = `${phoneNumber}`

    if (stateName && cityName) {
      locationStr += ` | ${stateName}, ${cityName}`
    } else if (stateName) {
      locationStr += ` | ${stateName}`
    } else if (cityName) {
      locationStr += ` | ${cityName}`
    }

    return locationStr
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          User Management
        </Typography>
        {isReseller && (
          <ActionButton startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Add User
          </ActionButton>
        )}
      </Box>
      {isAdmin && (<Box >
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
          <span style={{ color: "#008069" }}>Total End Users: ( {pagination.totalEndUsers || 0} ), </span> 
          <span style={{ color: "#008069" }}>Total Resellers: ( {pagination.totalResellers || 0} )</span> 
          
        </Typography>
      </Box>
      )}
     
      <Tabs value={activeTab} onChange={(e, newValue) => dispatch(setActiveTab(newValue))} sx={{ mb: 3 }}>
        <Tab label="Active" value="active" />
        <Tab label="Suspended" value="suspended" />
        <Tab label="Deleted" value="deleted" />
      </Tabs>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {isAdmin && (
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} onChange={handleRoleFilterChange} label="Role">
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="reseller">Reseller</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2">Records per page:</Typography>
          <FormControl sx={{ minWidth: 80 }}>
            <Select value={pageSize} onChange={handlePageSizeChange} size="small">
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>User</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Login ID</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Contact</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Role</StyledTableHeaderCell>
                  <StyledTableHeaderCell>Status</StyledTableHeaderCell>
                  {isAdmin && ( <StyledTableHeaderCell>Uplink</StyledTableHeaderCell>
                  )}
                  <StyledTableHeaderCell>DOM Credits</StyledTableHeaderCell>
                  <StyledTableHeaderCell>INT Credits</StyledTableHeaderCell>
                  <StyledTableHeaderCell>BUTTON Credits</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="right">Actions</StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length > 0 ? (
                  users.map((user, index) => (
                    <TableRow key={user._id} hover>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            src={user.profileImage}
                            alt={user.name}
                            sx={{ mr: 1.5, width: 32, height: 32, bgcolor: "#008069" }}
                          >
                            {getInitials(user.name)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500}>
                            {user.name} {user.role === "admin" ? "(Admin)" : ""}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                         <Typography variant="body2">U: {user.userName}</Typography>
                        {isAdmin && (
                            <Typography variant="body2">P: {user.password}</Typography>
                        ) }
                      </StyledTableCell>
                      <StyledTableCell>{getLocationString(user)}</StyledTableCell>
                      <StyledTableCell>
                        <RoleBadge
                          label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          size="small"
                          role={user.role}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusBadge
                          label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                          size="small"
                          status={user.status}
                        />
                      </StyledTableCell>
                       {isAdmin && (<StyledTableCell>{user.uplink || '-'}</StyledTableCell> )}
                      <StyledTableCell>{user.domesticCredits || 0}</StyledTableCell>
                      <StyledTableCell>{user.internationalCredits || 0}</StyledTableCell>
                      <StyledTableCell>{user.buttonCredits || 0}</StyledTableCell>
                      <StyledTableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          {activeTab === "active" && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenActionMenu(e, user)}
                                sx={{ color: "#008069" }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}

                          {activeTab === "suspended" && userInfo.user.role === "admin" && (
                            <ActionButton
                              onClick={() => handleUnsuspendUser(user._id)}
                              disabled={actionLoading}
                              size="small"
                            >
                              Unsuspend
                            </ActionButton>
                          )}

                          {activeTab === "deleted" && userInfo.user.role === "admin" && (
                            <ActionButton
                              onClick={() => handleRestoreUser(user._id)}
                              disabled={actionLoading}
                              size="small"
                            >
                              Restore
                            </ActionButton>
                          )}
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          color: "text.secondary",
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 48, mb: 1, color: "action.disabled" }} />
                        <Typography variant="body1" gutterBottom>
                          {searchQuery ? "No users found matching your search." : `No ${activeTab} users found.`}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
              {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalUsers)} of {pagination.totalUsers}{" "}
              users
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={!pagination.hasPrevPage}
                onClick={() => handlePageChange(pagination.currentPage - 1)}
              >
                Previous
              </Button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const pageNum = Math.max(1, pagination.currentPage - 2) + i
                if (pageNum <= pagination.totalPages) {
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === pagination.currentPage ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePageChange(pageNum)}
                      sx={{ minWidth: 40 }}
                    >
                      {pageNum}
                    </Button>
                  )
                }
                return null
              })}

              <Button
                variant="outlined"
                size="small"
                disabled={!pagination.hasNextPage}
                onClick={() => handlePageChange(pagination.currentPage + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleCloseActionMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            borderRadius: 1,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={() => handleOpenEditDialog(selectedUser)}>
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          Update
        </MenuItem>
        <MenuItem onClick={() => handleManageCredits(selectedUser?._id)}>
          <ListItemIcon>
            <CreditCardIcon fontSize="small" />
          </ListItemIcon>
          Manage Credits
        </MenuItem>

        {isAdmin && (
          <MenuItem onClick={() => handleOpenUplinkViewDialog(selectedUser)}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            Uplink
          </MenuItem>
        )}
        <MenuItem onClick={() => handleSuspendUser(selectedUser?._id)}>
          <ListItemIcon>
            <BlockIcon fontSize="small" color="warning" />
          </ListItemIcon>
          Suspend
        </MenuItem>
        <MenuItem onClick={() => handleOpenDeleteDialog(selectedUser)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
        {isAdmin && (
        <MenuItem onClick={() => handleOpenConfigDialog(selectedUser)}>
          <ListItemIcon>
            <Settings fontSize="small" color="error" />
          </ListItemIcon>
          Config
        </MenuItem>
        )}
      </Menu>

      {/* Create/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === "create" ? "Add User" : "Edit User"}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            disabled={actionLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={actionLoading}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

             
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="User Name"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                    disabled={actionLoading}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
            

              {/* Mobile */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  disabled={actionLoading}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Cutting Percentage */}
              {isAdmin && (<Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cutting Percentage"
                  name="cuttingPercentage"
                  value={formData.cuttingPercentage}
                  onChange={handleInputChange}
                  disabled={actionLoading}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              )}

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={actionLoading}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              
              {/* {isAdmin && ( */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={dialogMode === "create" ? "Password" : "Password (leave blank to keep unchanged)"}
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={actionLoading}
                    required={dialogMode === "create"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              {/* )} */}

              {/* User Type */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">User Type</FormLabel>
                  <RadioGroup row name="role" value={formData.role} onChange={handleInputChange}>
                    <FormControlLabel
                      value="reseller"
                      control={<Radio />}
                      label="Reseller"
                      disabled={actionLoading || (userInfo.user.role !== "admin" && formData.role === "admin")}
                    />
                    <FormControlLabel value="user" control={<Radio />} label="User" disabled={actionLoading} />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* State */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={actionLoading || loadingStates}>
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    label="State"
                  >
                    <MenuItem value="">
                      <em>Select State</em>
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state._id} value={state._id}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingStates && (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Loading states...
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={actionLoading || !formData.state || loadingCities}>
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    labelId="city-label"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    label="City"
                  >
                    <MenuItem value="">
                      <em>Select City</em>
                    </MenuItem>
                    {citiesList.map((city) => (
                      <MenuItem key={city._id} value={city._id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingCities && (
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Loading cities...
                      </Typography>
                    </Box>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={actionLoading} sx={{ color: "text.secondary" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={actionLoading}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#128C7E" },
                minWidth: 100,
              }}
            >
              {actionLoading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : dialogMode === "create" ? (
                "Add"
              ) : (
                "Save"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Config Dialog for User */}
      <Dialog open={configDialogOpen} onClose={handleCloseConfigDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Configure User: {userToConfigure?.name}
          <IconButton
            aria-label="close"
            onClick={handleCloseConfigDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            disabled={actionLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleConfigSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Number of Images */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Images"
                  name="numberOfImages"
                  type="number"
                  value={configFormData.numberOfImages}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Number of PDFs */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of PDFs"
                  name="numberOfPdfs"
                  type="number"
                  value={configFormData.numberOfPdfs}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Number of Videos */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Videos"
                  name="numberOfVideos"
                  type="number"
                  value={configFormData.numberOfVideos}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Divider />
              </Grid>
              {/* DOM Demo Max Campaign Allowed */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DOM Demo Max Campaign Allowed"
                  name="domDemoMaxCampaignAllowed"
                  type="number"
                  value={configFormData.domDemoMaxCampaignAllowed}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* DOM Max Campaign Allowed */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DOM Max Campaign Allowed"
                  name="domMaxCampaignAllowed"
                  type="number"
                  value={configFormData.domMaxCampaignAllowed}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* INT Demo Max Campaign Allowed */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="INT Demo Max Campaign Allowed"
                  name="intDemoMaxCampaignAllowed"
                  type="number"
                  value={configFormData.intDemoMaxCampaignAllowed}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* INT Max Campaign Allowed */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="INT Max Campaign Allowed"
                  name="intMaxCampaignAllowed"
                  type="number"
                  value={configFormData.intMaxCampaignAllowed}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* BTN Demo Max Campaign Allowed */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="BTN Demo Max Campaign Allowed"
                  name="btnDemoMaxCampaignAllowed"
                  type="number"
                  value={configFormData.btnDemoMaxCampaignAllowed}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* BTN Max Campaign Allowed */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="BTN Max Campaign Allowed"
                  name="btnMaxCampaignAllowed"
                  type="number"
                  value={configFormData.btnMaxCampaignAllowed}
                  onChange={handleConfigInputChange}
                  disabled={actionLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Divider />
              </Grid>
              {/* Auto Refund */}
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Auto Refund</FormLabel>
                  <RadioGroup
                    row
                    name="autoRefund"
                    value={configFormData.autoRefund}
                    onChange={handleConfigInputChange}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {/* Domestic Canpaign Create control */}
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Domestic Canpaign Create</FormLabel>
                  <RadioGroup
                    row
                    name="canCreateDomestic"
                    value={configFormData.canCreateDomestic}
                    onChange={handleConfigInputChange}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* International Canpaign Create control */}
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">International Canpaign Create</FormLabel>
                  <RadioGroup
                    row
                    name="canCreateInternational"
                    value={configFormData.canCreateInternational}
                    onChange={handleConfigInputChange}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Button Canpaign Create control */}
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Button Canpaign Create</FormLabel>
                  <RadioGroup
                    row
                    name="canCreateButton"
                    value={configFormData.canCreateButton}
                    onChange={handleConfigInputChange}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseConfigDialog} disabled={actionLoading} sx={{ color: "text.secondary" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={actionLoading}
              sx={{
                bgcolor: "#008069",
                "&:hover": { bgcolor: "#128C7E" },
                minWidth: 100,
              }}
            >
              {actionLoading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save Config"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Uplink Edit Dialog (existing) */}
      <UplinkDialog open={uplinkDialogOpen} onClose={() => setUplinkDialogOpen(false)} user={selectedUser} />

      {/* Uplink View Dialog (new) */}
      <UplinkViewDialog
        open={uplinkViewDialogOpen}
        onClose={handleCloseUplinkViewDialog}
        user={selectedUser}
        onOpenUplinkEdit={handleOpenUplinkDialogFromView}
      />
    </Box>
  )
}

export default UserManagement
